import { authApi } from '@/api/AuthApi'
import type { LoginFormData } from '@/components/LoginPage/LoginForm/LoginForm.types'
import {
	LoginCodeStatus,
	authUserFromCustomToken,
	sendLoginCodeToEmail,
} from '@/services/auth'
import { Button, ButtonText } from '@/ui/core/button'
import { Input } from '@/ui/core/input'
import { VStack } from '@/ui/core/stack'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { router } from 'expo-router'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import Toast from 'react-native-toast-message'
import { email, minLength, object, pipe, string, trim } from 'valibot'

const LoginSchema = object({
	emailAddress: pipe(
		string(),
		trim(),
		email('Login must be a valid email'),
		minLength(1)
	),
})

const AppReviewEmail = 'appreview@hiwally.co'

const AccountNotFoundMessage = `We couldn't find that email address in our system. Please use the email address you signed up with when creating your membership.`

const RateLimitedMessage =
	'You have made too many requests in a short period of time. Please wait a few minutes before trying again.'

const UnsubmittedLoginForm = ({ onSubmit = () => {} }) => {
	const [isSendingLoginCode, setIsSendingLoginCode] = useState(false)

	const {
		control,
		handleSubmit,
		formState: { isValid, errors },
	} = useForm<LoginFormData>({
		resolver: valibotResolver(LoginSchema),
	})

	const onLoginSubmit = async ({ emailAddress }: LoginFormData) => {
		if (!isValid) {
			Toast.show({
				type: 'error',
				text1: errors.emailAddress?.message,
			})
		}
		setIsSendingLoginCode(true)

		if (emailAddress === AppReviewEmail) {
			const customToken = await authApi.fetchDemoAccountToken()

			if (customToken) {
				await authUserFromCustomToken(customToken.token, () => {
					router.navigate('/')
				})
				setIsSendingLoginCode(false)
				return
			}
		}

		const loginCodeStatus = await sendLoginCodeToEmail(emailAddress)
		setIsSendingLoginCode(false)

		if (loginCodeStatus === LoginCodeStatus.RATE_LIMITED) {
			Toast.show({
				type: 'error',
				text1: RateLimitedMessage,
			})
		}

		if (loginCodeStatus === LoginCodeStatus.NOT_FOUND) {
			Toast.show({
				type: 'error',
				text1: AccountNotFoundMessage,
			})
		}

		if (loginCodeStatus === LoginCodeStatus.SUCCESSFUL) {
			onSubmit()
		}
	}

	return (
		<VStack className="justify-center w-full">
			<VStack gap={12}>
				<Controller
					name="emailAddress"
					control={control}
					render={({ field: { onChange, onBlur, value } }) => (
						<Input
							placeholder="Email"
							value={value}
							onBlur={onBlur}
							onChangeText={onChange}
							inputMode="email"
							autoComplete="email"
							autoCapitalize="none"
							autoCorrect={false}
						/>
					)}
				/>
				<Button
					className="w-full"
					isLoading={isSendingLoginCode}
					disabled={!isValid}
					onPress={handleSubmit(onLoginSubmit)}
				>
					<ButtonText>Log In</ButtonText>
				</Button>
			</VStack>
		</VStack>
	)
}

export { UnsubmittedLoginForm }
